import type { FormEvent } from "react";
import { useState } from "react";
import type { UpdatePasswordPayload } from "api";
import cn from "classnames";
import { PasswordInput } from "components/ui/PasswordInput";
import { useFlash } from "hooks/useFlash";

const MIN_LENGTH = 10;

type PasswordFormProps = {
  onSubmit: (payload: UpdatePasswordPayload) => Promise<string | null>;
};

export const PasswordForm = ({ onSubmit }: PasswordFormProps) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { shown: done, flash: flashDone, clear: clearDone } = useFlash();
  const [error, setError] = useState<string | null>(null);

  const currentBad = touched && current === "";
  const nextBad = touched && next.length < MIN_LENGTH;
  const repeatBad = touched && repeat !== next;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    clearDone();

    if (current === "" || next.length < MIN_LENGTH || repeat !== next) {
      return;
    }

    setSaving(true);
    setError(null);

    const message = await onSubmit({
      current_password: current,
      password: next,
      password_confirmation: repeat,
    });

    setSaving(false);

    if (message !== null) {
      setError(message);
      return;
    }

    setCurrent("");
    setNext("");
    setRepeat("");
    setTouched(false);
    flashDone();
  };

  return (
    <form className="pcx" noValidate onSubmit={(event) => void submit(event)}>
      <div className="pcx__top">
        <div>
          <div className="pcx__h">Password</div>
          <p className="pcx__sub">At least {MIN_LENGTH} characters. You stay signed in on this device.</p>
        </div>
      </div>

      <div className="pc-form">
        <label className={cn("pcf", { "is-bad": currentBad })}>
          <span>Current password</span>
          <PasswordInput
            name="cur"
            value={current}
            placeholder="Enter your current password"
            autoComplete="current-password"
            onChange={setCurrent}
          />
          <small className="pcf__err">Enter your current password</small>
        </label>

        <label className={cn("pcf", { "is-bad": nextBad })}>
          <span>New password</span>
          <PasswordInput
            name="new"
            value={next}
            placeholder={`At least ${MIN_LENGTH} characters`}
            autoComplete="new-password"
            onChange={setNext}
          />
          <small className="pcf__err">Use at least {MIN_LENGTH} characters</small>
        </label>

        <label className={cn("pcf", { "is-bad": repeatBad })}>
          <span>Repeat new password</span>
          <PasswordInput
            name="rep"
            value={repeat}
            placeholder="Repeat it"
            autoComplete="new-password"
            onChange={setRepeat}
          />
          <small className="pcf__err">Both entries must match</small>
        </label>
      </div>

      <div className="pc-acts">
        <button type="submit" className="pcbtn pcbtn--main" disabled={saving}>
          {saving ? "Updating…" : "Update password"}
        </button>
        {done && <span className="pc-saved">Password updated</span>}
        {error && <span className="pc-failed">{error}</span>}
      </div>
    </form>
  );
};
